import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Activity, Clock, CheckCircle, Calendar, AlertCircle } from "lucide-react";

export default function MonitoringPage() {
  const { t } = useI18n();
  const [schedule, setSchedule] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/monitoring/schedule"),
      api.get("/monitoring/runs"),
    ]).then(([s, r]) => {
      setSchedule(s.data);
      setRuns(r.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const freqLabel = (f) => f === "daily" ? t("monitoring.daily") : f === "weekly" ? t("monitoring.weekly") : t("monitoring.monthly");

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="monitoring-page">
      <h1 className="text-2xl font-bold">{t("monitoring.title")}</h1>

      <Card className="bg-card border-border border-l-4 border-l-blue-500" data-testid="monitoring-schedule-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <Calendar className="w-6 h-6 text-blue-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">{t("monitoring.auto_title")}</h3>
              {schedule?.scheduled ? (
                <>
                  <p className="text-muted-foreground mt-1">{t("monitoring.auto_desc")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-5">
                    <div className="bg-accent/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">{t("monitoring.frequency")}</p>
                      <p className="text-sm font-bold mt-1 capitalize">{freqLabel(schedule.frequency)}</p>
                    </div>
                    <div className="bg-accent/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">{t("monitoring.schedule")}</p>
                      <p className="text-sm font-bold mt-1">{schedule.schedule_description}</p>
                    </div>
                    <div className="bg-accent/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">{t("monitoring.col_products")}</p>
                      <p className="text-sm font-bold mt-1 font-mono-data">{schedule.product_count}</p>
                    </div>
                    <div className="bg-accent/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">{t("monitoring.package")}</p>
                      <p className="text-sm font-bold mt-1">{schedule.plan_name}</p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground mt-1">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {t("monitoring.no_sub")}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{t("monitoring.history")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("monitoring.col_status")}</TableHead>
                <TableHead>{t("monitoring.col_date")}</TableHead>
                <TableHead className="text-right">{t("monitoring.col_products")}</TableHead>
                <TableHead className="text-right">{t("monitoring.col_competitors")}</TableHead>
                <TableHead className="text-right">{t("monitoring.col_changes")}</TableHead>
                <TableHead>{t("monitoring.col_duration")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{t("monitoring.no_runs")}</TableCell></TableRow>
              ) : (
                runs.map((run) => {
                  const duration = run.started_at && run.completed_at
                    ? `${Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000)}s`
                    : "-";
                  return (
                    <TableRow key={run.id} className="table-row-hover" data-testid={`run-${run.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CheckCircle className={`w-4 h-4 ${run.status === "completed" ? "text-emerald-500" : "text-amber-500"}`} />
                          <Badge variant={run.status === "completed" ? "default" : "secondary"} className={run.status === "completed" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>
                            {run.status === "completed" ? t("monitoring.completed") : run.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono-data text-sm">{run.started_at ? new Date(run.started_at).toLocaleString("nl-NL") : "-"}</TableCell>
                      <TableCell className="text-right font-mono-data">{run.products_scanned || 0}</TableCell>
                      <TableCell className="text-right font-mono-data">{run.competitors_found || 0}</TableCell>
                      <TableCell className="text-right font-mono-data">{run.price_changes || 0}</TableCell>
                      <TableCell className="font-mono-data text-sm">{duration}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
